<template>
  <v-dialog v-model="dialog" persistent width="50%">
    <v-btn color="secondary" dark slot="activator" class="mx-0">
      <v-icon small>$vuetify.icons.rms_create</v-icon>
      {{ $t("rms.button.create") }}
    </v-btn>
    <v-card class="pa-3">
      <v-card-title>
        <div>
          <div class="title">{{ $t("company.create.title") }}</div>
          <div class="py-1 body-1 grey--text text--darken-1 subheading">
            {{ $t("screener.company.create.subtitle") }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          class="mx-0 px-0"
          small
          dark
          icon
          color="grey"
          @click="dialog = false"
        >
          <v-icon small>$vuetify.icons.rms_close</v-icon>
        </v-btn>
      </v-card-title>
      <v-stepper class="elevation-0" v-model="step">
        <v-stepper-header flat class="elevation-0 px-0">
          <v-stepper-step
            step="1"
            :complete="step > 1"
            :rules="[() => details_validation]"
            editable
            class="px-3"
            >{{ $t("screener.basic_info.title") }}</v-stepper-step
          >
          <v-divider></v-divider>
          <v-stepper-step
            step="2"
            :complete="step > 2"
            :rules="[() => slug_selection_validation]"
            editable
            class="px-3"
          >
            {{ $t("company.create.step.slug_selection.title") }}
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step
            step="3"
            :complete="step > 3"
            :rules="[() => declaration_validation]"
            editable
            class="px-3"
          >
            {{ $t("company.create.step.declaration.title") }}
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step
            step="4"
            :complete="step > 4"
            :rules="[() => logo_validation]"
            editable
            class="px-3"
          >
            {{ $t("company.create.step.logo.title") }}
          </v-stepper-step>
        </v-stepper-header>
        <v-stepper-items class="pa-3">
          <v-stepper-content step="1" class="pa-0">
            <v-form ref="basic_detail">
              <!-- Title -->
              <v-text-field
                id="title"
                :label="$t('rms.field.title.label')"
                name="title"
                :rules="[
                  rules.required,
                  rules.min_length(6),
                  rules.max_length(250),
                ]"
                counter="250"
                prepend-icon="$vuetify.icons.rms_bank"
                type="text"
                v-model="title"
                class="custom-placeholer-color"
              >
                <template v-slot:append>
                  <v-tooltip top color="white">
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <div class="grey--text text--darken-3 body-1">
                      {{ $t("company.save.title.tooltip") }}
                    </div>
                  </v-tooltip>
                </template></v-text-field
              >
              <!-- Description -->
              <v-text-field
                id="notes"
                :label="$t('company.save.description.title')"
                name="notes"
                counter="250"
                prepend-icon="$vuetify.icons.rms_agreement"
                type="text"
                :rules="[rules.max_length(250)]"
                v-model="notes"
                class="custom-placeholer-color"
              >
                <template v-slot:append>
                  <v-tooltip top color="white">
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <div class="grey--text text--darken-3 body-1">
                      {{ $t("company.save.description.tooltip") }}
                    </div>
                  </v-tooltip>
                </template></v-text-field
              >
              <!-- Meta keywords -->
              <v-text-field
                id="meta_keywords"
                :label="$t('company.save.meta_keywords.title')"
                :placeholder="$t('company.save.meta_keywords.placeholder')"
                name="notes"
                prepend-icon="mdi-alphabetical"
                type="text"
                v-model="meta_info.keywords"
                class="custom-placeholer-color"
              >
                <template v-slot:append>
                  <v-tooltip top color="white">
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <div class="grey--text text--darken-3 body-1">
                      {{ $t("company.save.meta_keywords.tooltip") }}
                    </div>
                  </v-tooltip>
                </template></v-text-field
              >
              <!-- Currency -->
              <v-autocomplete
                v-model="currency"
                :prefix="getCurrencySymbol()"
                :items="DEFINES.MONETIZATION.CURRENCIES"
                item-text="name"
                item-value="value"
                prepend-icon="$vuetify.icons.screener_money"
                :rules="[rules.required]"
                :label="$t('company.save.currency.title')"
              >
                <template v-slot:append>
                  <v-tooltip top color="white">
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <div class="grey--text text--darken-3 body-1">
                      {{ $t("company.save.currency.tooltip") }}
                    </div>
                  </v-tooltip>
                </template>
                <template v-slot:selection="data">{{
                  data.item.value
                }}</template>
              </v-autocomplete>
              <div>
                <span class="greyDarken3--text font-weight-bold"
                  >{{
                    $t("screener.company.create.currency.note.title")
                  }}&nbsp;:&nbsp;</span
                >
                <span class="greyDarken3--text"
                  ><i>{{
                    $t("screener.company.create.currency.note.description")
                  }}</i></span
                >
              </div>
              <div>
                <v-autocomplete
                  prepend-icon="mdi-earth"
                  v-model="timezone"
                  :items="DEFINES.SUPPORTED_TIMEZONES"
                  color="primary"
                  :label="$t('screener.admin.company.select_timezone')"
                  :rules="[rules.required]"
                >
                  <template v-slot:selection="data">
                    {{ data.item }}
                    {{ momentTz.tz(data.item).format("Z z") }}
                  </template>
                  <template v-slot:item="data">
                    <template
                      v-if="
                        typeof data.item !== 'object' &&
                          !DEFINES.IGNORED_TIMEZONES.includes(data.item)
                      "
                    >
                      {{ data.item }}
                      {{ momentTz.tz(data.item).format("Z z") }}
                    </template>
                  </template>
                </v-autocomplete>
              </div>
              <div>
                <v-text-field
                  :value="stripe_descriptor"
                  prepend-icon="mdi-wallet"
                  @input="
                    toUpperCaseDescriptors(
                      'statement_descriptor_suffix',
                      $event
                    )
                  "
                  class="mb-3"
                  color="primary"
                  type="text"
                  :rules="[
                    rules.required,
                    rules.min_length(2),
                    rules.max_length(10),
                    rules.paymentDescriptor,
                  ]"
                  :label="
                    $t(
                      'screener.payment_setting.statement_descriptor_suffix.label'
                    )
                  "
                  persistent-hint
                  :hint="
                    $t(
                      'screener.payment_setting.statement_descriptor_suffix.hint'
                    )
                  "
                ></v-text-field>
                <div class="greyDarken2--text">
                  <h5>
                    For more detail about descriptors,
                    <a
                      href="https://stripe.com/docs/statement-descriptors"
                      target="_blank"
                      rel="noopener noreferrer"
                      >Click Here</a
                    >
                  </h5>
                </div>
              </div>
            </v-form>
          </v-stepper-content>
          <!-- SLUG SELECTION STEP -->
          <v-stepper-content step="2" class="pa-0">
            <v-form ref="slug_selection">
              <!-- Slug -->
              <v-text-field
                v-model="slug"
                color="primary"
                :label="$t('rms.field.slug.label')"
                prepend-icon="mdi-earth"
                :rules="[
                  rules.required,
                  rules.min_length(2),
                  rules.max_length(15),
                  rules.subdomain,
                ]"
                :hint="$t('company.save.slug.hint')"
                counter="15"
              >
                <template v-slot:append>
                  <v-tooltip top color="white">
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <div class="grey--text text--darken-3 body-1">
                      {{ $t("company.save.slug.tooltip") }}
                    </div>
                  </v-tooltip>
                </template></v-text-field
              >
              <div class="py-3" v-if="slug && slug.length">
                <div class="greyDarken2--text pb-2">
                  {{ $t("screener.company.create.domain.title") }}
                </div>
                <div class="title">
                  <span class="greyDarken3--text primary--text"
                    >{{ protocol }}{{ slugify }}{{ domain }}</span
                  >
                </div>
              </div>
              <div v-if="showSlugError">
                <span class="error--text">
                  {{ showSlugError }}
                </span>
              </div>
            </v-form>
          </v-stepper-content>
          <!-- DECLARATION STEP -->
          <v-stepper-content step="3" class="pa-0">
            <v-form ref="declaration">
              <!-- Creator role -->
              <v-autocomplete
                v-model="creator_role"
                :items="DEFINES.COMPANY_CREATOR_ROLE"
                item-text="text"
                item-value="value"
                prepend-icon="mdi-account-star"
                :label="$t('company.save.creator_role.title')"
                :rules="[rules.required]"
              >
                <template v-slot:append>
                  <v-tooltip top color="white">
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <div class="grey--text text--darken-3 body-1">
                      {{ $t("company.save.creator_role.tooltip") }}
                    </div>
                  </v-tooltip>
                </template>
              </v-autocomplete>
              <!-- Declararion -->
              <v-checkbox
                v-model="declaration"
                value="1"
                color="primary"
                :rules="[rules.required]"
              >
                <template v-slot:label>
                  <div>
                    {{ $t("company.save.declaration.title") }}
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <a
                          target="_blank"
                          :href="DEFINES.MOVIES_EVERYWHERE_TERMS_PAGE_LINK"
                          @click.stop
                          v-on="on"
                        >
                          {{ $t("terms.title") }}
                        </a>
                      </template>
                      {{ $t("rms.field.tnc.label") }}
                    </v-tooltip>
                  </div>
                </template>
              </v-checkbox>
            </v-form>
          </v-stepper-content>
          <!-- Upload Logo -->
          <v-stepper-content step="4" class="pa-0">
            <v-form ref="logo" v-if="company">
              <v-layout row wrap class="px-3 pb-4">
                <!-- Upload logo -->
                <v-flex xs12 md6 class="pa-0">
                  <v-container
                    bg
                    fill-height
                    grid-list-md
                    text-xs-center
                    py-0
                    pl-0
                  >
                    <v-layout row wrap align-center>
                      <v-flex class="pa-0">
                        <v-card @click="showDropzoneLogoDialog = true">
                          <v-img
                            v-if="company.logo_url"
                            height="12vmin"
                            contain
                            :src="company.logo_url"
                          ></v-img>
                          <v-icon
                            color="blue-grey lighten-4"
                            style="font-size: 70px"
                            v-else
                            >mdi-cloud-upload</v-icon
                          >
                          <v-card-title class="justify-center">
                            <v-chip color="primary white--text font-weight-bold"
                              >{{ $t("screener.company.profile.upload_logo") }}
                            </v-chip>
                          </v-card-title>
                        </v-card>
                      </v-flex>
                    </v-layout>
                  </v-container>
                  <SingleFileUploader
                    key="upload_logo"
                    v-model="showDropzoneLogoDialog"
                    :title="$t('screener.company.profile.upload_logo')"
                    :warning="$t('screener.admin.drag_and_drop.warning')"
                    :attribute="'logo_url'"
                    :collection="this.DEFINES.COLLECTION_PUBLIC"
                    :dropzoneOptions="dropzoneOptions('uploadLogo')"
                    @setThumbnal="setThumbnail"
                    @onError="showError"
                  ></SingleFileUploader>
                </v-flex>
                <!-- Upload favicon -->
                <v-flex xs12 md6 class="pa-0">
                  <v-container
                    bg
                    fill-height
                    grid-list-md
                    text-xs-center
                    py-0
                    pl-0
                  >
                    <v-layout row wrap align-center>
                      <v-flex class="pa-0">
                        <v-card @click="showDropzoneLogoDialog = true">
                          <v-img
                            v-if="company.favicon_url"
                            height="12vmin"
                            contain
                            :src="company.favicon_url"
                          ></v-img>
                          <v-icon
                            color="blue-grey lighten-4"
                            style="font-size: 70px"
                            v-else
                            >mdi-cloud-upload</v-icon
                          >
                          <v-card-title class="justify-center">
                            <v-chip color="primary white--text font-weight-bold"
                              >{{
                                $t("screener.company.profile.upload_favicon")
                              }}
                            </v-chip>
                          </v-card-title>
                        </v-card>
                      </v-flex>
                    </v-layout>
                  </v-container>
                  <SingleFileUploader
                    key="upload_favicon"
                    v-model="showDropzoneLogoDialog"
                    :title="$t('screener.company.profile.upload_favicon')"
                    :warning="$t('screener.admin.drag_and_drop.warning')"
                    :attribute="'favicon_url'"
                    :collection="this.DEFINES.COLLECTION_PUBLIC"
                    :dropzoneOptions="dropzoneOptions('uploadFavicon')"
                    @setThumbnal="setThumbnail"
                    @onError="showError"
                  ></SingleFileUploader>
                </v-flex>
              </v-layout>
            </v-form>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <v-card-actions class="px-3">
        <v-spacer></v-spacer>
        <v-btn color="grey" outline @click.native="reset()">
          {{ $t("company.create.cancel.button") }}
        </v-btn>
        <v-btn v-if="step > 1" color="grey" outline @click.stop="step = --step">
          {{ $t("screener.btn.back") }}
        </v-btn>

        <v-btn
          v-if="step < 3"
          outline
          color="secondary"
          flat
          @click.stop="step = ++step"
        >
          {{ $t("rms.button.continue") }}
        </v-btn>

        <v-btn
          v-if="step == 3"
          outline
          color="secondary"
          flat
          @click.stop="create()"
        >
          {{ $t("rms.button.create") }}
        </v-btn>
        <v-btn
          v-if="step == max_step && this.company"
          outline
          color="secondary"
          flat
          @click.stop="update()"
        >
          {{ $t("rms.button.menu.upload") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import rules from "@/validation";
import DEFINES from "@/defines";
import { getCurrencySymbol } from "@/helper";
import SingleFileUploader from "@/components/media/SingleFileUploader";
import { buildHeaders } from "@/api/_manager";
import i18n from "@/i18n";

export default {
  name: "CreateCompanyDialog",
  components: { SingleFileUploader },
  data() {
    return {
      rules,
      title: "",
      slug: "",
      domain: this.DEFINES.AVAIL_APP_DOMAIN,
      protocol: this.DEFINES.AVAIL_APP_PROTOCOL,
      dialog: false,
      // Default currency USD
      currency: this.DEFINES.MONETIZATION.CURRENCIES[0].value,
      notes: null,
      meta_info: {
        keywords: [],
      },
      creator_role: null,
      declaration: null,
      step: 1,
      max_step: 4,
      details_validation: true,
      slug_selection_validation: true,
      declaration_validation: true,
      logo_validation: true,
      timezone: null,
      stripe_descriptor: null,
      showDropzoneLogoDialog: false,
      company: null,
      showSlugError: null,
    };
  },
  computed: {
    slugify() {
      // On resetting for, slug becomes undefined
      if (this.slug && this.slug.length) {
        return this.slug
          .split(" ")
          .join("-")
          .toLowerCase();
      }
    },
  },
  methods: {
    validate() {
      if (!this.$refs.basic_detail.validate()) {
        this.details_validation = false;
        this.step = 1;
        return false;
      }
      if (!this.$refs.slug_selection.validate()) {
        this.slug_selection_validation = false;
        this.step = 2;
        return false;
      }

      //disallowed slugs
      const disallowedDomains = ["www", "app", "api", "watch", "vendor", "db"];
      if (disallowedDomains.indexOf(this.slug.toLowerCase()) > -1) {
        this.slug_selection_validation = false;
        this.showSlugError = i18n.t("company.slug.disallowed.error");
        this.step = 2;
        return false;
      }
      this.showSlugError = null;

      if (!this.$refs.declaration.validate()) {
        this.step = 3;
        this.declaration_validation = false;
        return false;
      }
      if (
        this.company &&
        (!this.company.logo_url || !this.company.favicon_url)
      ) {
        this.step = 4;
        this.logo_validation = false;
        return false;
      }
      return true;
    },
    async create() {
      if (!this.validate()) {
        return false;
      }

      if (this.company) {
        this.step++;
        return false;
      }
      try {
        //create company and update the list of company

        var data = await this.$store.dispatch(
          "company/create",
          this._preparePayload()
        );

        this.details_validation = this.slug_selection_validation = this.declaration_validation = true;
        this.company = data.company;
        this.step++;
      } catch (error) {
        this.$store.dispatch("app/showMessage", {
          message: error.message,
          color: "error",
        });
      }
    },
    dropzoneOptions(uploading_relative_path) {
      return {
        acceptedFiles: "image/*",
        url: `${this.DEFINES.BASE_API_URL}/member/company/${this.company.id}/${uploading_relative_path}`,
        thumbnailWidth: 200,
        maxFilesize: 1, //MB
        chunking: true,
        chunkSize: 2000000, // Bytes
        destroyDropzone: false,
        headers: buildHeaders().headers,
      };
    },
    setThumbnail(event) {
      this.company[event.on] = event.value;
    },
    async showError(event) {
      try {
        await this.$store.dispatch("app/showMessage", {
          message: event.message,
          color: "error",
        });
      } catch (error) {
        this.$store.dispatch("app/showMessage", {
          message: error.message,
          color: "error",
        });
      }
    },
    async update() {
      try {
        if (!this.validate() || !this.company) {
          return false;
        }
        var data = await this.$store.dispatch("company/update", this.company);

        this.reset();

        this.$store.dispatch("company/onAfterCreate", data);
      } catch (error) {
        this.$store.dispatch("app/showMessage", {
          message: error.message,
          color: "error",
        });
      }
    },
    _preparePayload() {
      // Keywords should be of array type
      this.meta_info.keywords =
        this.meta_info.keywords.length > 0 &&
        typeof this.meta_info.keywords == "string"
          ? this.meta_info.keywords.split(",")
          : this.meta_info.keywords;

      // Company access domain
      var full_domain = this.protocol + this.slugify + this.domain;
      return {
        title: this.title,
        slug: this.slug.toLowerCase(),
        notes: this.notes,
        meta_info: this.meta_info,
        domain: full_domain,
        currency: this.currency,
        creator_role: this.creator_role,
        timezone: this.timezone,
        payment_setting: {
          stripe: { statement_descriptor_suffix: this.stripe_descriptor },
        },
      };
    },
    getCurrencySymbol() {
      if (this.currency && this.currency.length) {
        return getCurrencySymbol(this.currency);
      }
    },
    reset() {
      this.dialog = false;
      this.$refs.basic_detail.reset();
      this.$refs.slug_selection.reset();
      this.$refs.declaration.reset();
      this.showSlugError = null;
      this.step = 1;
    },

    toUpperCaseDescriptors(property, value) {
      if (!value) {
        this.stripe_descriptor = value;
        return;
      }
      this.stripe_descriptor = value.toUpperCase();
    },
  },
};
</script>
